import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiImage } from 'react-icons/fi';
import { useApp } from '../../context/AppContext';
import {
  CHAT_THREAD_BACKGROUND_PRESETS,
  getChatMessageCardPresetStorageValue,
  getChatThreadPresetStorageValue,
  resolveChatThreadImageUrl,
  setChatMessageCardPresetStorageValue,
  setChatThreadPresetStorageValue,
} from '../../utils/chatThreadBackgrounds';
import {
  clearAllUserThreadBackgroundStorage,
  clearUserThreadBackgroundStorage,
  compressImageFileToDataUrl,
  getUserThreadBackgroundDataUrl,
  setUserThreadBackgroundDataUrl,
} from '../../utils/chatThreadUserBackgroundStorage';

function roleIsAdminOrHr(user) {
  const r = String(user?.role || '').trim().toLowerCase();
  return r === 'admin' || r === 'hr';
}

export default function ChatThreadBackgroundPicker({ compact = false }) {
  const { state } = useApp();
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState(() => getChatThreadPresetStorageValue());
  const [cardPreset, setCardPreset] = useState(() => getChatMessageCardPresetStorageValue());
  const [adminUrl, setAdminUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingUserBg, setUploadingUserBg] = useState(false);
  const [hasUserBg, setHasUserBg] = useState(false);
  const [userBgPreview, setUserBgPreview] = useState('');
  const wrapRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const desktopPanelRef = useRef(null);
  /** viewport coords for fixed portal panel (desktop) */
  const [desktopPanelPos, setDesktopPanelPos] = useState({ top: 0, right: 0 });

  const isAdmin = roleIsAdminOrHr(state.user);
  /** Свой фон: отдельный слот для мобильного чата и для десктопного */
  const userBgSurface = compact ? 'mobile' : 'desktop';

  const refreshAdminUrl = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAdminUrl('');
      return;
    }
    try {
      const r = await fetch('/api/chat/thread-background', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json().catch(() => ({}));
      setAdminUrl(d?.imageUrl ? String(d.imageUrl).trim() : '');
    } catch {
      setAdminUrl('');
    }
  }, []);

  useEffect(() => {
    if (open) {
      setPreset(getChatThreadPresetStorageValue());
      setCardPreset(getChatMessageCardPresetStorageValue());
      void (async () => {
        const u = await getUserThreadBackgroundDataUrl(userBgSurface);
        setHasUserBg(!!u);
        setUserBgPreview(u || '');
      })();
      if (isAdmin) refreshAdminUrl();
    }
  }, [open, isAdmin, refreshAdminUrl, userBgSurface]);

  // Важно: «click», а не «mousedown». Иначе mousedown на document срабатывает раньше onClick на кнопке
  // пресета, панель закрывается и размонтируется — выбор не применяется.
  useEffect(() => {
    const onDoc = (e) => {
      const t = e.target;
      if (wrapRef.current?.contains(t)) return;
      if (mobilePanelRef.current?.contains(t)) return;
      if (desktopPanelRef.current?.contains(t)) return;
      setOpen(false);
    };
    if (open) document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const updateDesktopPanelPos = useCallback(() => {
    const el = wrapRef.current;
    if (!el || typeof window === 'undefined') return;
    const rect = el.getBoundingClientRect();
    setDesktopPanelPos({
      top: rect.bottom + 8,
      right: Math.max(12, window.innerWidth - rect.right),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open || compact) return;
    updateDesktopPanelPos();
    const onWin = () => updateDesktopPanelPos();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [open, compact, updateDesktopPanelPos]);

  const choosePreset = (id) => {
    void (async () => {
      if (id) {
        await clearUserThreadBackgroundStorage(userBgSurface);
        setChatThreadPresetStorageValue(id);
      } else {
        await clearAllUserThreadBackgroundStorage();
        setChatThreadPresetStorageValue('');
      }
      setPreset(getChatThreadPresetStorageValue());
      const u = await getUserThreadBackgroundDataUrl(userBgSurface);
      setHasUserBg(!!u);
      setUserBgPreview(u || '');
    })();
  };

  const chooseCardPreset = (id) => {
    if (id) setChatMessageCardPresetStorageValue(id);
    else setChatMessageCardPresetStorageValue('');
    setCardPreset(getChatMessageCardPresetStorageValue());
  };

  const handleAdminUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !String(file.type || '').startsWith('image/')) {
      alert('Выберите изображение (JPEG, PNG, GIF, WebP)');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Нужна авторизация');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await fetch('/api/chat/thread-background/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ошибка загрузки');
      await refreshAdminUrl();
      window.dispatchEvent(new CustomEvent('chat-thread-bg-updated'));
    } catch (err) {
      alert(err.message || 'Не удалось загрузить');
    } finally {
      setUploading(false);
    }
  };

  const handleUserCustomUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !String(file.type || '').startsWith('image/')) {
      alert('Выберите изображение (JPEG, PNG, GIF, WebP)');
      return;
    }
    setUploadingUserBg(true);
    try {
      const dataUrl = await compressImageFileToDataUrl(file);
      await setUserThreadBackgroundDataUrl(dataUrl, userBgSurface);
      setChatThreadPresetStorageValue('');
      setPreset('');
      setHasUserBg(true);
      setUserBgPreview(dataUrl);
    } catch (err) {
      alert(err.message || 'Не удалось сохранить фото');
    } finally {
      setUploadingUserBg(false);
    }
  };

  const clearUserCustomPhoto = async () => {
    try {
      await clearUserThreadBackgroundStorage(userBgSurface);
      setHasUserBg(false);
      setUserBgPreview('');
    } catch (err) {
      alert(err.message || 'Не удалось убрать фото');
    }
  };

  const clearAdminImage = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch('/api/chat/thread-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ imageUrl: '' }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Ошибка');
      }
      setAdminUrl('');
      window.dispatchEvent(new CustomEvent('chat-thread-bg-updated'));
    } catch (err) {
      alert(err.message || 'Не удалось сбросить');
    }
  };

  const light = CHAT_THREAD_BACKGROUND_PRESETS.filter((p) => p.group === 'light');
  const mid = CHAT_THREAD_BACKGROUND_PRESETS.filter((p) => p.group === 'mid');
  const dark = CHAT_THREAD_BACKGROUND_PRESETS.filter((p) => p.group === 'dark');

  const presetGroups = [
    { label: 'Светлые', list: light },
    { label: 'Полутёмные', list: mid },
    { label: 'Тёмные', list: dark },
  ];

  const renderGradientPicker = (selectedId, onSelect) =>
    presetGroups.map(({ label, list }) => (
      <div key={label} style={{ marginBottom: 12 }}>
        <div style={{ fontSize: '0.72em', color: '#64748b', marginBottom: 6, fontWeight: 700 }}>
          {label}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 10px',
                borderRadius: 10,
                border: selectedId === p.id ? '2px solid #43e97b' : '1px solid #334155',
                background: '#0f172a',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 36,
                  height: 24,
                  borderRadius: 6,
                  border: '1px solid #475569',
                  flexShrink: 0,
                  background: p.gradient,
                }}
              />
              <span style={{ fontSize: '0.82em', color: '#e2e8f0', fontWeight: 600 }}>{p.label}</span>
            </button>
          ))}
        </div>
      </div>
    ));

  const columnStyle = {
    flex: '1 1 240px',
    minWidth: 0,
  };

  const panelBody = (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 16,
          alignItems: 'flex-start',
          marginBottom: 4,
        }}
      >
        <div style={columnStyle}>
          <div style={{ fontWeight: 800, color: '#e2e8f0', marginBottom: 8, fontSize: '0.95em' }}>
            Фон чата
          </div>
          <p style={{ margin: '0 0 10px', fontSize: '0.75em', color: '#94a3b8', lineHeight: 1.45 }}>
            Фон окна переписки. Своя картинка ниже относится только к{' '}
            {compact ? 'этому телефону' : 'этому компьютеру'} в браузере; на другом устройстве можно
            выбрать другое фото. «По умолчанию» сбрасывает и телефон, и ПК к общему фону админа или
            стилю приложения.
          </p>
          <button
            type="button"
            onClick={() => choosePreset('')}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: 10,
              borderRadius: 10,
              border: !preset && !hasUserBg ? '2px solid #43e97b' : '1px solid #475569',
              background: !preset && !hasUserBg ? 'rgba(67,233,123,0.15)' : 'rgba(255,255,255,0.06)',
              color: '#f1f5f9',
              fontWeight: 700,
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '0.88em',
            }}
          >
            По умолчанию (фон чата)
          </button>
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 10,
              border: hasUserBg && !preset ? '2px solid #a78bfa' : '1px solid #475569',
              background: hasUserBg && !preset ? 'rgba(167,139,250,0.12)' : 'rgba(255,255,255,0.04)',
            }}
          >
            <div style={{ fontWeight: 700, color: '#e2e8f0', marginBottom: 6, fontSize: '0.85em' }}>
              Своё фото {compact ? '(только мобильный чат)' : '(только чат на ПК)'}
            </div>
            <p style={{ margin: '0 0 8px', fontSize: '0.7em', color: '#94a3b8', lineHeight: 1.4 }}>
              Отдельно от фото в {compact ? 'версии для компьютера' : 'мобильной версии'}. Сжимается
              перед сохранением.
            </p>
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              disabled={uploadingUserBg || uploading}
              onChange={handleUserCustomUpload}
              style={{ width: '100%', fontSize: '0.78em', marginBottom: 8 }}
            />
            {uploadingUserBg && (
              <div style={{ fontSize: '0.75em', color: '#94a3b8' }}>Обработка…</div>
            )}
            {userBgPreview && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={userBgPreview}
                  alt=""
                  style={{
                    width: '100%',
                    maxHeight: 100,
                    objectFit: 'cover',
                    borderRadius: 8,
                    border: '1px solid #334155',
                  }}
                />
                <button
                  type="button"
                  onClick={() => void clearUserCustomPhoto()}
                  style={{
                    marginTop: 8,
                    background: 'transparent',
                    border: 'none',
                    color: '#f87171',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '0.82em',
                  }}
                >
                  Убрать своё фото
                </button>
              </div>
            )}
          </div>
          {renderGradientPicker(preset, choosePreset)}
        </div>

        <div
          style={{
            ...columnStyle,
            ...(compact
              ? {
                  flex: '1 1 100%',
                  borderTop: '1px solid rgba(71, 85, 105, 0.5)',
                  paddingTop: 14,
                  marginTop: 4,
                  paddingLeft: 0,
                }
              : {
                  borderLeft: '1px solid rgba(71, 85, 105, 0.5)',
                  paddingLeft: 16,
                }),
          }}
        >
          <div style={{ fontWeight: 800, color: '#e2e8f0', marginBottom: 8, fontSize: '0.95em' }}>
            Карточка сообщения
          </div>
          <p style={{ margin: '0 0 10px', fontSize: '0.75em', color: '#94a3b8', lineHeight: 1.45 }}>
            Градиент пузырька. Светлый — тёмный текст; полутёмный/тёмный — белый текст (имя, время, текст).
          </p>
          <button
            type="button"
            onClick={() => chooseCardPreset('')}
            style={{
              width: '100%',
              padding: '10px 12px',
              marginBottom: 12,
              borderRadius: 10,
              border: !cardPreset ? '2px solid #38bdf8' : '1px solid #475569',
              background: !cardPreset ? 'rgba(56,189,248,0.12)' : 'rgba(255,255,255,0.06)',
              color: '#f1f5f9',
              fontWeight: 700,
              cursor: 'pointer',
              textAlign: 'left',
              fontSize: '0.88em',
            }}
          >
            По умолчанию (стиль пузырьков)
          </button>
          {renderGradientPicker(cardPreset, chooseCardPreset)}
        </div>
      </div>

      {isAdmin && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px dashed #475569',
          }}
        >
          <div style={{ fontWeight: 800, color: '#fbbf24', marginBottom: 8, fontSize: '0.9em' }}>
            Админ: фон для всех (по умолчанию)
          </div>
          <p style={{ margin: '0 0 8px', fontSize: '0.72em', color: '#94a3b8', lineHeight: 1.4 }}>
            У каждого свой градиент и свои фото (отдельно на телефоне и на ПК) — они перекрывают этот
            общий фон.
          </p>
          <input
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            disabled={uploading}
            onChange={handleAdminUpload}
            style={{ width: '100%', fontSize: '0.78em', marginBottom: 8 }}
          />
          {uploading && <div style={{ fontSize: '0.75em', color: '#94a3b8' }}>Загрузка…</div>}
          {adminUrl ? (
            <div style={{ marginTop: 8 }}>
              <img
                src={resolveChatThreadImageUrl(adminUrl)}
                alt=""
                style={{
                  width: '100%',
                  maxHeight: 90,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '1px solid #334155',
                }}
              />
              <button
                type="button"
                onClick={clearAdminImage}
                style={{
                  marginTop: 8,
                  background: 'transparent',
                  border: 'none',
                  color: '#f87171',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '0.82em',
                }}
              >
                Убрать фото (только градиент по умолчанию)
              </button>
            </div>
          ) : null}
        </div>
      )}
    </>
  );

  const mobileOverlay =
    open &&
    compact &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        role="presentation"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 100050,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 12,
          boxSizing: 'border-box',
        }}
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) setOpen(false);
        }}
      >
        <div
          ref={mobilePanelRef}
          data-chat-thread-bg-picker-modal=""
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            width: 'min(100%, 720px)',
            maxWidth: '100%',
            maxHeight: 'min(88vh, 920px)',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            padding: 14,
            borderRadius: 14,
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(109, 213, 237, 0.35)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
          }}
        >
          {panelBody}
        </div>
      </div>,
      document.body
    );

  const desktopPortal =
    open &&
    !compact &&
    typeof document !== 'undefined' &&
    createPortal(
      <>
        <div
          role="presentation"
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100049,
            background: 'rgba(0,0,0,0.35)',
          }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        />
        <div
          ref={desktopPanelRef}
          data-chat-thread-bg-picker-desktop=""
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: desktopPanelPos.top,
            right: desktopPanelPos.right,
            width: 'min(720px, calc(100vw - 24px))',
            minWidth: 280,
            maxHeight: 'min(78vh, 720px)',
            overflowY: 'auto',
            padding: 14,
            borderRadius: 14,
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
            border: '1px solid rgba(109, 213, 237, 0.35)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
            zIndex: 100050,
            boxSizing: 'border-box',
          }}
        >
          {panelBody}
        </div>
      </>,
      document.body
    );

  return (
    <div ref={wrapRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="Фон чата"
        aria-expanded={open}
        aria-haspopup="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 0 : 6,
          padding: compact ? '8px' : '8px 12px',
          borderRadius: compact ? '50%' : 12,
          border: '1px solid rgba(109, 213, 237, 0.45)',
          background: open ? 'rgba(102, 126, 234, 0.35)' : 'rgba(255,255,255,0.08)',
          color: '#6dd5ed',
          cursor: 'pointer',
          minWidth: compact ? 40 : undefined,
          minHeight: compact ? 40 : undefined,
        }}
      >
        <FiImage size={compact ? 20 : 18} />
        {!compact && <span style={{ fontSize: '0.88em', fontWeight: 600 }}>Фон</span>}
      </button>
      {mobileOverlay}
      {desktopPortal}
    </div>
  );
}
